import React, { useCallback, useEffect, useRef, useState } from 'react';
import { errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { JOB_TONES, STATUSES, asValue, jobUrl, queueUrl, shiftYmd, todayYmd, when } from './deviceData';
import JobShell from './JobShell';
import JsonView from './JsonView';

const list = (v) => (Array.isArray(v) ? v : []);
const FULL = 100000;       // the editors in the job dialog show every line: the dialog has no scroll bars of its own

// One job in full: what it ran, with what, what it returned and its log.
function JobDialog({ account, deviceType, deviceId, id, request, onClose }) {
  const requestRef = useRef(request);
  requestRef.current = request;
  const [job, setJob] = useState({ state: 'loading' });
  // quiet: a refresh of what is already shown (the timer below) neither blanks it nor replaces it with an error if one call fails.
  const load = useCallback(async (quiet) => {
    if (!quiet) setJob((j) => ({ ...j, state: 'loading' }));
    try {
      const r = await requestRef.current('GET', jobUrl(account, deviceType, deviceId, id));
      setJob({ state: 'ok', item: r.data?.item || {} });
    } catch (ex) {
      if (!quiet) setJob({ state: 'error', error: errorText(ex) });
    }
  }, [account, deviceType, deviceId, id]);
  useEffect(() => { load(); }, [load]);
  // A queued or running job can change at any moment (a worker claims it, finishes it), so it is read again every 4 seconds until it ends, as in the
  // queue site.
  const live = ['queued', 'running'].includes(job.item?.status);
  useEffect(() => {
    if (!live) return undefined;
    const timer = setInterval(() => load(true), 4000);
    return () => clearInterval(timer);
  }, [live, load]);
  const item = job.item || {};
  const facts = [['functionpath', item.functionpath], ['id', item.id], ['status', item.status], ['queue', item.queue], ['priority', item.priority],
    ['created', when(item.created)], ['started', when(item.started)], ['completed', when(item.completed)], ['claimedBy', item.claimedBy]]
    .filter(([, v]) => v !== undefined && v !== null && v !== '');

  return (
    <JobShell title={item.functionpath || 'Job'} ariaLabel="Job" onClose={onClose} onRefresh={() => load()}>
      {job.state === 'loading' && <div><span className="v2-spin" role="status" aria-label="Loading" /></div>}
      {job.state === 'error' && <div className="v2-alert err" role="alert">{job.error}</div>}
      {job.state === 'ok' && (
        <>
          <dl className="v2-jobfacts" aria-label="Job details">
            {facts.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{String(v)}</dd></div>)}
          </dl>
          {item.splat !== undefined && item.splat !== null && <div><h3 className="v2-h3">Splat</h3><JsonView value={asValue(item.splat)} label="Splat" maxHeight={FULL} /></div>}
          {item.results !== undefined && item.results !== null && item.results !== '' && <div><h3 className="v2-h3">Results</h3><JsonView value={asValue(item.results)} label="Results" maxHeight={FULL} /></div>}
          {item.log && <div><h3 className="v2-h3">Log</h3><pre className="v2-json-text" aria-label="Log">{item.log}</pre></div>}
        </>
      )}
    </JobShell>
  );
}

// The jobs in this device's queue, newest first: pick a day (or all time) and a status, and open one to see it in full.
export default function QueueTab({ account, deviceType, deviceId, request }) {
  const requestRef = useRef(request);
  requestRef.current = request;
  const [ymd, setYmd] = useState(todayYmd());          // '' is all time
  const [status, setStatus] = useState('');
  const [auto, setAuto] = useState(false);
  const [jobs, setJobs] = useState({ state: 'loading', items: [] });
  const [open, setOpen] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [asOf, setAsOf] = useState('');

  // quiet: the timer's refresh keeps what is shown if one call fails.
  const load = useCallback(async (quiet) => {
    if (!quiet) setRefreshing(true);
    try {
      const r = await requestRef.current('GET', queueUrl(account, deviceType, deviceId, ymd, status));
      setJobs({ state: 'ok', items: list(r.data?.items) });
      setAsOf(when(new Date().toISOString()));
    } catch (ex) {
      if (!quiet) setJobs({ state: 'error', error: errorText(ex), items: [] });
    } finally {
      setRefreshing(false);
    }
  }, [account, deviceType, deviceId, ymd, status]);
  useEffect(() => { setJobs((j) => ({ ...j, state: 'loading' })); load(); }, [load]);
  useEffect(() => {
    if (!auto) return undefined;
    const timer = setInterval(() => load(true), 10000);
    return () => clearInterval(timer);
  }, [auto, load]);

  return (
    <section aria-label="Queue">
      <div className="v2-bar tight">
        <h2 className="v2-h2">Queue</h2>
        <button type="button" className="v2-btn" aria-label="Previous day" disabled={!ymd} onClick={() => setYmd(shiftYmd(ymd, -1))}>&larr;</button>
        <input className="v2-input" type="date" aria-label="Day" value={ymd} onChange={(e) => setYmd(e.target.value)} style={{ maxWidth: 170 }} />
        <button type="button" className="v2-btn" aria-label="Next day" disabled={!ymd} onClick={() => setYmd(shiftYmd(ymd, 1))}>&rarr;</button>
        <button type="button" className="v2-btn" onClick={() => setYmd(todayYmd())}>Today</button>
        <button type="button" className="v2-btn" aria-pressed={!ymd} onClick={() => setYmd('')}>All time</button>
      </div>
      <div className="v2-bar tight">
        <div role="group" aria-label="Status">
          {['', ...STATUSES].map((s) => (
            <button key={s || 'all'} type="button" className={`v2-chipbtn${status === s ? ' on' : ''}`} aria-pressed={status === s} onClick={() => setStatus(s)}>{s || 'All'}</button>
          ))}
        </div>
        <span className="grow" />
        <label className="v2-check"><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} />Auto-refresh</label>
        {asOf && <span className="v2-sub">{`as of ${asOf}`}</span>}
        <button type="button" className="v2-btn" disabled={refreshing} onClick={() => load()}>{refreshing ? 'Refreshing...' : 'Refresh'}</button>
      </div>
      {jobs.state === 'loading' && <div className="v2-loading"><span className="v2-spin" role="status" aria-label="Loading" /></div>}
      {jobs.state === 'error' && <div className="v2-alert err" role="alert">{jobs.error}</div>}
      {jobs.state === 'ok' && (jobs.items.length === 0 ? <div className="v2-empty">No jobs.</div> : (
        <table className="v2-table" aria-label="Queue jobs">
          <thead><tr><th>Status</th><th>Function path</th><th>Priority</th><th>Created</th><th>Completed</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {jobs.items.map((j) => (
              <tr key={j.id}>
                <td><span className={`v2-pill ${JOB_TONES[j.status] || 'none'}`}>{j.status}</span></td>
                <td><code data-testid="job-path">{j.functionpath}</code></td>
                <td>{j.priority}</td>
                <td>{when(j.created)}</td>
                <td>{when(j.completed)}</td>
                <td className="nowrap"><button type="button" className="v2-btn" aria-label={`View ${j.functionpath} ${j.id}`} onClick={() => setOpen(j.id)}>View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}
      {open && <JobDialog account={account} deviceType={deviceType} deviceId={deviceId} id={open} request={request} onClose={() => setOpen('')} />}
    </section>
  );
}
